import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AnalyticsService } from './core/services/analytics/analytics.service';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
} from '@azure/msal-browser';
import { filter, Subject, Subscription, takeUntil } from 'rxjs';
import { CookiebotListenerService } from './core/services/cookiebot-listener.service';
import { AuthService } from './core/services/auth.service';
import { TranslocoService } from '@jsverse/transloco';
import { ProductFruitsIntegrationService } from './core/services/product-fruits-integration.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly msalService = inject(MsalService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly cookiebotListenerService = inject(CookiebotListenerService);
  private readonly authService = inject(AuthService);
  private readonly translocoService = inject(TranslocoService);
  private readonly productFruitsIntegrationService = inject(
    ProductFruitsIntegrationService,
  );

  private readonly destroy$: Subject<void> = new Subject();
  private langSub: Subscription;

  ngOnInit(): void {
    this.subscribeToCookieConsentChanges();
    this.msalService
      .handleRedirectObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: AuthenticationResult | null) => {
        if (result) {
          const { oid } = this.authService.extractUserInfo(result);
          if (!this.analyticsService.isUserIdentified && oid) {
            this.analyticsService.identify(oid);
          }
          this.analyticsService.track('logged_in');
          this.productFruitsIntegrationService.load(oid);
        }
      });

    this.observeLogoutEvents();

    this.langSub = this.translocoService.langChanges$.subscribe(() => {
      void this.authService.getMsalAuthenticationResult().then((result) => {
        const { oid } = this.authService.extractUserInfo(result);
        if (oid) {
          this.productFruitsIntegrationService.load(oid);
        }
      });
    });

    if (!window.productFruits) {
      void this.authService.getMsalAuthenticationResult().then((result) => {
        const { oid } = this.authService.extractUserInfo(result);
        if (oid) {
          this.productFruitsIntegrationService.load(oid);
        }
      });
    }
  }

  private subscribeToCookieConsentChanges() {
    this.cookiebotListenerService.statisticsConsent$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (consentGiven) => {
        if (consentGiven === null) {
          return;
        }
        this.analyticsService.setConsent(consentGiven);

        if (!this.analyticsService.isUserIdentified) {
          const { oid } = this.authService.extractUserInfo(
            await this.authService.getMsalAuthenticationResult(),
          );
          if (oid) {
            this.analyticsService.identify(oid);
          }
        }
      });
  }

  private observeLogoutEvents(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (event: EventMessage) => event.eventType === EventType.LOGOUT_SUCCESS,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.analyticsService.track('logged_out');
        this.analyticsService.reset();
      });
  }

  ngOnDestroy(): void {
    this.langSub.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
